import React from "react";
import PropTypes from "prop-types";
import Immutable from "immutable";
import ImmutablePropTypes from "react-immutable-proptypes";
import classnames from "classnames";

import InfoTooltip from "components/Common/InfoTooltip";
import SvgIcon from "components/Common/SvgIcon";
import { NO_OP_FUNCTION } from "services/helpers";

import colors from "stylesheets/utilities/colors.scss";
import "./style.scss";

const BLOCK_NAME = "Select";

class Select extends React.Component {
  /** @param {Object} props */
  constructor(props) {
    super(props);

    this.state = {
      activeIndex: 0,
      isVisible: false,
      elementWidth: null,
      elementHeight: null,
      bottomAligned: false,
      hoverItemValue: null,
      onSubMenu: false,
      subActiveIndex: null,
      subMenuState: Immutable.Map(props.options.map(option => [option.value, false])),
      subHoverItemValue: null
    };

    /**
     * @constant
     * @type {Number}
     */
    this.RESULTS_MAX_WIDTH = 280;

    /**
     * @constant
     * @type {Number}
     */
    this.RESULTS_MIN_WIDTH = 140;

    /**
     * @constant
     * @type {Number}
     */
    this.RESULTS_MAX_HEIGHT = props.resultsMaxHeight;

    this.listElementRef = React.createRef();
    this.selectElementRef = React.createRef();
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleSelectKeyDown = this.handleSelectKeyDown.bind(this);
    this.handleToggleDropdown = this.handleToggleDropdown.bind(this);
    this.handleMousedownOutside = this.handleMousedownOutside.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
  }

  /** */
  componentDidMount() {
    const selectElementNode = this.selectElementRef.current;

    if (!selectElementNode) {
      return;
    }

    const { width, height } = selectElementNode.getBoundingClientRect();

    this.setState({
      elementWidth: width,
      elementHeight: height
    });
  }

  /**
   * @param {Object} prevProps
   * @param {Object} prevState
   */
  componentDidUpdate(prevProps, prevState) {
    const {
      hoverItemValue, activeIndex, subHoverItemValue, subActiveIndex
    } = this.state;

    const { customSelectedValue } = this.props;

    this.handleResultsToggle(prevState);

    if (prevState.hoverItemValue !== hoverItemValue) {
      this.removeHightlight("keyboard-main");
    }

    if (prevState.subHoverItemValue !== subHoverItemValue) {
      this.removeHightlight("keyboard-sub");
    }

    if (prevState.activeIndex !== activeIndex) {
      this.removeHightlight("mouse-main");
    }

    if (prevState.subActiveIndex && prevState.subActiveIndex !== subActiveIndex) {
      this.removeHightlight("mouse-sub");
    }

    if (prevProps.customSelectedValue !== customSelectedValue) {
      this.handleCloseWithCustomSubMenu();
    }
  }

  /**
   * Returns the selected option based on the value prop
   *
   * @returns {Object}
   */
  getSelectedOption() {
    const {
      options, value, subMenuValue, customParentValue
    } = this.props;

    let selectedOption;

    if (subMenuValue) {
      options.forEach((option) => {
        option.subMenu.forEach((subOption) => {
          if (subOption.value === subMenuValue) {
            selectedOption = subOption.selected;
          }
        });
      });
    } else if (customParentValue) {
      options.forEach((option) => {
        if (customParentValue === option.value) {
          selectedOption = option.customSelectedElement;
        }
      });
    } else {
      selectedOption = options.find(option => option.value === value);
    }

    return selectedOption;
  }

  /**
   * Returns the element in the list corresponding to the active index
   *
   * @param {Number} activeIndex
   *
   * @returns {HTMLElement|null}
   */
  getActiveElementNode(activeIndex) {
    if (!this.listElementRef.current) {
      return null;
    }

    return this.listElementRef.current.childNodes.item(
      activeIndex
    );
  }

  /**
   * Returns text to be rendered inside the select button
   *
   * @returns {ReactElement|String}
   */
  getSelectText() {
    const {
      showIconAsSelected,
      showIconBesideSelected,
      placeholder,
      placeholderIcon,
      placeholderIconColor,
      disabled
    } = this.props;
    const selectedOption = this.getSelectedOption();
    let rendered;

    if (!selectedOption) {
      rendered = placeholder;

      if (placeholderIcon) {
        rendered = (
          <div className={`${BLOCK_NAME}__select__placeholder-rich`}>
            <div className={`${BLOCK_NAME}__select__placeholder-rich__icon`}>
              <SvgIcon icon={placeholderIcon} fillColor={placeholderIconColor} />
            </div>
            <div className={`${BLOCK_NAME}__select__placeholder-rich__label`}>
              {placeholder}
            </div>
          </div>
        );
      }

      return rendered;
    }

    let icon;

    if (selectedOption.icon) {
      icon = (
        <SvgIcon
          icon={selectedOption.icon}
          customClassName={`${BLOCK_NAME}__select__icon`}
          fillColor={disabled ? colors.colorGrey3 : colors.colorBlack}
        />
      );
    }

    if (showIconAsSelected && selectedOption.icon) {
      rendered = icon;
    } else if (
      showIconBesideSelected &&
      selectedOption.icon &&
      selectedOption.label
    ) {
      rendered = (
        <>
          {icon}
          <span>{selectedOption.label}</span>
        </>
      );
    } else if (selectedOption.label) {
      rendered = selectedOption.label;
    } else if (selectedOption && typeof selectedOption === "string") {
      rendered = selectedOption;
    } else if (selectedOption && typeof selectedOption === "object") {
      rendered = selectedOption;
    }

    return rendered;
  }

  /**
   * Render sub-menu
   * @param {Object} parent
   * @param {Object} subMenu
   */
  getOptionsSubMenu(parent, subMenu) {
    const {
      subMenuValue
    } = this.props;

    const { subActiveIndex, subHoverItemValue } = this.state;

    const iconFillColor = colors.colorUIPrimary;

    return subMenu.map((subitem, i) => {
      const isActive = subitem.value === subMenuValue;
      const isSelected = (i === subActiveIndex) || (subHoverItemValue === subitem.value);

      return (
        <React.Fragment
          key={subitem.label}
        >
          <li
            className={classnames(`${BLOCK_NAME}__list__item`, {
              [`${BLOCK_NAME}__list__item--active`]: isActive,
              [`${BLOCK_NAME}__list__item--focus`]: isSelected
            })}
          >
            <button
              className={`${BLOCK_NAME}__list__item__button`}
              onClick={() => this.handleClose(parent, subitem)}
              onMouseEnter={() => this.handleMenuHover(subitem, "sub")}
              type="button"
            >
              <span className={`${BLOCK_NAME}__list__item__button__label`}>
                {subitem.label}
              </span>
              {isActive && (
                <SvgIcon
                  icon="Checkmark"
                  customClassName={`${BLOCK_NAME}__list__item__button__icon`}
                  fillColor={iconFillColor}
                />
              )}
            </button>
          </li>
        </React.Fragment>
      );
    });
  }

  /**
   * Returns list of options elements
   *
   * @returns {Array}
   */
  getOptionsList() {
    const {
      value,
      toggleValues,
      options,
      useRadioButtons,
      plainDropdown
    } = this.props;

    const { activeIndex, hoverItemValue, subMenuState } = this.state;

    return options.map((option, i) => {
      const isActive = value === option.value ||
        (option.type === "toggle" && toggleValues.get(option.value));
      const { hoverIcon } = option;
      const isSelected = (i === activeIndex) || (hoverItemValue === option.value);
      const isReservedOption = option.type === "sub-header";
      const isDivider = option.type === "divider";
      let iconFillColor = colors.colorBlack;

      if (option.disabled) {
        iconFillColor = colors.colorGrey3;
      } else if (isActive) {
        iconFillColor = colors.colorUIPrimary;
      }

      if (isReservedOption) {
        return (
          <li
            key={`${option.type}-${option.label}`}
            className={`${BLOCK_NAME}__list__item ${BLOCK_NAME}__list__item--sub-header`}
          >
            {Boolean(option.icon) && (
              <SvgIcon
                icon={option.icon}
                customClassName={`${BLOCK_NAME}__list__item__option-icon`}
                fillColor={iconFillColor}
              />
            )}
            <span className={`${BLOCK_NAME}__list__item__label`}>
              {option.label}
            </span>
          </li>
        );
      }

      if (isDivider) {
        return (
          <li
            // Don't know what to do about this one
            // eslint-disable-next-line react/no-array-index-key
            key={`divider-${i}`}
            className={`${BLOCK_NAME}__list__item ${BLOCK_NAME}__list__item--divider`}
          />
        );
      }

      return (
        <React.Fragment
          key={option.value}
        >
          <li
            className={classnames(`${BLOCK_NAME}__list__item`, {
              [`${BLOCK_NAME}__list__item--active`]: isActive,
              [`${BLOCK_NAME}__list__item--focus`]: isSelected,
              [`${BLOCK_NAME}__list__item--no-sub-menu`]: !option.subMenu,
              [`${BLOCK_NAME}__list__item--disabled`]: option.disabled
            })}
            onMouseEnter={() => this.handleMenuHover(option, "main")}
          >
            <button
              className={`${BLOCK_NAME}__list__item__button`}
              onMouseDown={() => {
                if (!option.subMenu && !option.customSubMenu) {
                  this.handleMouseDown(i);
                }
              }}
              onClick={() => {
                if (!option.subMenu && !option.customSubMenu) {
                  this.handleClose(option, null);
                }
              }}
              type="button"
            >
              {Boolean(option.icon) && (
                <SvgIcon
                  icon={option.icon}
                  customClassName={`${BLOCK_NAME}__list__item__button__option-icon`}
                  fillColor={iconFillColor}
                />
              )}
              <span className={`${BLOCK_NAME}__list__item__button__label`}>
                {option.label}
              </span>
              {option.tooltip && (
                <InfoTooltip
                  blurb={option.tooltip}
                  iconDefault="QuestionCircle"
                  iconClicked="QuestionCircleFilled"
                  absolute
                  inModal
                />
              )}
              {(option.subMenu || option.customSubMenu) && (
                <SvgIcon
                  icon="TriangleArrowRight"
                  customClassName={`${BLOCK_NAME}__list__item__button__icon`}
                  height={8}
                  width={5}
                />
              )}
              {isActive && !plainDropdown && !option.subMenu && !option.customSubMenu && (
                <SvgIcon
                  icon={useRadioButtons ? "CircleRadio" : "Checkmark"}
                  customClassName={`${BLOCK_NAME}__list__item__button__icon`}
                  fillColor={iconFillColor}
                />
              )}
              {!isActive && hoverIcon && (
                <SvgIcon
                  icon={hoverIcon}
                  customClassName={`${BLOCK_NAME}__list__item__hover-icon`}
                  fillColor={colors.colorUIPrimary}
                />
              )}
            </button>
            {subMenuState.get(option.value) && !option.customSubMenu && (
              <div className={`${BLOCK_NAME}__list-sub-menu-wrapper`}>
                <ul className={`${BLOCK_NAME}__list ${BLOCK_NAME}__list--sub-menu`}>
                  {this.getOptionsSubMenu(option, option.subMenu)}
                </ul>
              </div>
            )}
            {option.customSubMenu && subMenuState.get(option.value) && (
              <div className={`${BLOCK_NAME}__list-sub-menu-wrapper`}>
                {option.customSubMenu}
              </div>
            )}
          </li>
        </React.Fragment>
      );
    });
  }


  /**
   * @param {Number} index
   * @param {Object} options
   */
  setIndexValue(index, options) {
    const newActiveElement = this.getActiveElementNode(index);

    if (!newActiveElement || !this.listElementRef.current) {
      return;
    }

    // if you hit the bottom, loop back up to the top
    if (index === 0) {
      this.listElementRef.current.scrollTop = 0;
    }

    // if you hit the top, loop back down to the bottom
    if (index === options.length - 1) {
      this.listElementRef.current.scrollTop = newActiveElement.offsetHeight * (options.length - 1);
    }

    // the offset of active element from top of viewport is greater than element 2*offsetHeight
    if (newActiveElement.offsetTop -
        this.listElementRef.current.scrollTop >
        2 * newActiveElement.offsetHeight
    ) {
      // scroll down
      // scroll the viewport the offset height of the element
      this.listElementRef.current.scrollTop += newActiveElement.offsetHeight;
    // scroll up
    } else if (newActiveElement.offsetTop -
      this.listElementRef.current.scrollTop <
      2 * newActiveElement.offsetHeight
    ) {
      this.listElementRef.current.scrollTop -= newActiveElement.offsetHeight;
    }
  }

  /** */
  handleCloseWithCustomSubMenu() {
    this.setState({
      isVisible: false
    });
  }

  /**
   * Removes keyboard hover when mouse is used for navigation
   * @param {String} type
   */
  removeHightlight(type) {
    const { options, value } = this.props;
    const { hoverItemValue, subMenuState } = this.state;
    let newHoverItemMap = Immutable.Map();

    switch (type) {
      case "keyboard-main":
        subMenuState.keySeq().toArray().forEach((subMenu) => {
          if (subMenu === hoverItemValue) {
            newHoverItemMap = newHoverItemMap.set(hoverItemValue, true);
          } else {
            newHoverItemMap = newHoverItemMap.set(subMenu, false);
          }
        });
        this.setState({
          subMenuState: newHoverItemMap,
          activeIndex: options.findIndex(option => option.value === value)
        });
        break;
      case "keyboard-sub":
        this.setState({
          subActiveIndex: null
        });
        break;
      case "mouse-main":
        this.setState({
          hoverItemValue: null
        });

        break;
      case "mouse-sub":
        this.setState({
          subHoverItemValue: null
        });
        break;
      default:
    }
  }

  /**
   * Update mouse hovered item value in state
   * @param {Object} option
   * @param {String} menu
   */
  handleMenuHover(option, menu) {
    switch (menu) {
      case "main":
        if (option.subMenu || option.customSubMenu) {
          this.setState({
            hoverItemValue: option.value
          });
        } else {
          this.setState({
            hoverItemValue: null
          });
        }

        break;
      case "sub":
        this.setState({
          subHoverItemValue: option.value
        });
        break;
      default:
    }
  }

  /**
   * Handle keyDown event to leverage arrow keys and enter keypresses
   *
   * @param {Object} event
   */
  handleKeyDown(event) {
    const { activeIndex, onSubMenu, subActiveIndex } = this.state;
    const { options } = this.props;
    event.stopPropagation();

    if (event.keyCode === 38 ||
      event.keyCode === 37 ||
      event.keyCode === 40 ||
      event.keyCode === 13 ||
      event.keyCode === 32 ||
      event.keyCode === 39 ||
      event.keyCode === 27
    ) {
      event.preventDefault();
    }


    if (event.keyCode === 38 && activeIndex >= 0) {
      // Arrow up
      this.handleArrow("up");
    } else if (event.keyCode === 40 && activeIndex <= options.length - 1) {
      // Arrow down
      this.handleArrow("down");
    } else if (event.keyCode === 37) {
      // Arrow left
      this.handleArrow("left");
    } else if (event.keyCode === 13 || event.keyCode === 32) {
      // Enter key and Space key
      if (options[activeIndex].subMenu) {
        if (onSubMenu) {
          this.handleClose(options[activeIndex], options[activeIndex].subMenu[subActiveIndex]);
        } else {
          this.handleArrow("right");
        }
      } else {
        this.handleClose(options[activeIndex]);
      }
    } else if (event.keyCode === 39 || event.keyCode === 32) {
      // Arrow right and Space key
      this.handleArrow("right");
    } else if (event.keyCode === 27) {
      // Esc key
      this.setState({
        isVisible: false
      });
    }
  }

  /**
   * Handle setting next active index, as well as scrolling dropdown window if needed
   *
   * @param {String} dir can be either "up" or "down" (if given something else, it exits)
   */
  handleArrow(dir) {
    const {
      activeIndex, subActiveIndex, onSubMenu, subMenuState
    } = this.state;
    const { options } = this.props;
    let directionOffset;

    switch (dir) {
      case "up":
        directionOffset = -1;
        break;
      case "down":
        directionOffset = 1;
        break;
      case "left":
        if (onSubMenu) {
          this.setState({
            subMenuState: subMenuState.set(options[activeIndex].value, false),
            onSubMenu: false,
            subActiveIndex: null
          });
        }

        break;
      case "right":
        if (options[activeIndex].subMenu) {
          this.setState({
            subMenuState: subMenuState.set(options[activeIndex].value, true),
            onSubMenu: true,
            subActiveIndex: 0,
            hoverItemValue: null
          });
        }

        break;
      default:
        return;
    }

    if (onSubMenu) {
      this.setState(
        prevState => ({
          subActiveIndex: this.skipNonClickableElements(prevState.subActiveIndex, directionOffset)
        }), this.setIndexValue(subActiveIndex, options[activeIndex])
      );
    } else {
      this.setState(
        prevState => ({
          activeIndex: this.skipNonClickableElements(prevState.activeIndex, directionOffset)
        }), this.setIndexValue(activeIndex, options)
      );
    }
  }


  /**
   * Handle activeIndex state change, as well as binding/unbinding
   * event listeners when dropdown is opened/closed
   *
   * @param {Object} prevState
   */
  handleResultsToggle(prevState) {
    const { isVisible } = this.state;
    const { value, options } = this.props;

    if (prevState.isVisible !== isVisible) {
      if (isVisible) {
        this.setState({
          activeIndex:
            options.findIndex(
              option => option.value === value
            ) || 0
        }, () => {
          const { activeIndex } = this.state;
          const activeNode = this.getActiveElementNode(activeIndex);

          if (!activeNode) {
            return;
          }

          // center the selected element in the dropdown
          if (this.listElementRef.current) {
            this.listElementRef.current.scrollTop = (
              activeNode.offsetTop - 2 * activeNode.offsetHeight
            );
          }
        });

        document.addEventListener(
          "mousedown",
          this.handleMousedownOutside,
          true
        );
        document.addEventListener("keydown", this.handleKeyDown, true);
      } else {
        document.removeEventListener(
          "mousedown",
          this.handleMousedownOutside,
          true
        );
        document.removeEventListener("keydown", this.handleKeyDown, true);
      }
    }
  }

  /**
   * Handles toggling the dropdown
   *
   * @param {Object} event
   */
  handleToggleDropdown(event) {
    const { isVisible } = this.state;
    const { forceRightAligned, forceBottomAligned } = this.props;

    if (
      !this.listElementRef.current ||
      !(
        this.listElementRef.current &&
        this.listElementRef.current.contains(event.target)
      )
    ) {
      const clientRect = event.target.getBoundingClientRect();
      event.stopPropagation();
      /**
       * 320 represents the max-height of the drop
       * 280 is the max-width
       */
      this.clearSubMenuState();
      this.setState({
        isVisible: !isVisible,
        hoverItemValue: null,
        rightAligned:
        forceRightAligned || window.innerWidth - clientRect.right <= this.RESULTS_MAX_WIDTH,
        bottomAligned:
        forceBottomAligned || clientRect.top + this.RESULTS_MAX_HEIGHT >= window.innerHeight
      });
    }
  }


  /**
   * Trigger handleToggleDropdown if arrow up, down arrow, enter, or space pressed
   *
   * @param {Object} event
   */
  handleSelectKeyDown(event) {
    if (
      event.keyCode === 40 || // arrow down
      event.keyCode === 38 || // arrow up
      event.keyCode === 13 || // enter
      event.keyCode === 32 // space
    ) {
      this.handleToggleDropdown(event);
    }
  }

  /**
   * Increment the activeIndex such that it skips un-clickable elements (sub-headers)
   *
   * @param {Number} index
   * @param {Number} step
   *
   * @returns {Number}
   */
  skipNonClickableElements(index, step) {
    const { options } = this.props;
    const { activeIndex } = this.state;
    let tmp;

    if (index + step === options.length) {
      tmp = 0;
    } else if (index + step < 0) {
      tmp = options.length - 1;
    } else {
      tmp = index + step;
    }

    const newIndex = tmp;
    const option = options[newIndex];

    if (!option) {
      return activeIndex;
    }

    return (option.type === "sub-header" || option.disabled)
      ? this.skipNonClickableElements(newIndex, step)
      : newIndex;
  }

  /**
   * Handle closing the dropdown when clicking off of the Select component
   * @param {Object} event
   */
  handleMousedownOutside(event) {
    if (this.selectElementRef.current && !this.selectElementRef.current.contains(event.target)) {
      event.stopPropagation();
      this.setState({
        isVisible: false,
        hoverItemValue: null
      });

      this.clearSubMenuState();
    }
  }

  /**
   * Close all submenu by clearing subMenuState
   */
  clearSubMenuState() {
    const { options } = this.props;
    this.setState({
      subMenuState: Immutable.Map(options.map(option => [option.value, false])),
      subActiveIndex: null,
      subHoverItemValue: null,
      onSubMenu: false
    });
  }

  /**
   * Handle setting the activeIndex when clicking on a list element
   *
   * @param {Number} index
   */
  handleMouseDown(index) {
    this.setState({
      activeIndex: index
    });
  }

  /**
   * Handle closing the dropdown, then triggering a change event with the selected option
   *
   * @param {Object} option
   * @param {Object} subMenu
   */
  handleClose(option, subMenu) {
    const { onToggleChange, toggleValues, onChange } = this.props;

    /**
     * If the option being clicked is a toggle, don't close the dropdown
     */
    this.clearSubMenuState();
    this.setState(
      {
        isVisible: option.type === "toggle",
        hoverItemValue: null
      },
      () => {
        // Need to use setTimeout to avoid "render clobbering" slowdown
        setTimeout(() => {
          if (option.type === "toggle") {
            onToggleChange(
              option.value,
              !toggleValues.get(option.value)
            );
          } else if (subMenu) {
            onChange([option.value, subMenu.value]);
          } else {
            onChange(option.value);
          }
        }, 0);

        // Re-focus button
        if (this.selectElementRef.current) {
          this.selectElementRef.current.focus();
        }
      }
    );
  }

  /**
   * Returns options to be rendered inside the "Select__select__hidden-options" element
   * This is needed so that the width of the select button always stays the same
   *
   * @returns {Array}
   */
  renderAllHiddenOptions() {
    const { options, subMenuValue } = this.props;

    if (subMenuValue) {
      return (
        <span className={`${BLOCK_NAME}__select__hidden-options__option`} key={`${subMenuValue}--hidden`}>
          {this.getSelectText()}
        </span>
      );
    }

    return options.map(option => (
      <span className={`${BLOCK_NAME}__select__hidden-options__option`} key={`${option.value}--hidden`}>
        {option.label}
      </span>
    ));
  }

  /**
   * Render the dropdown
   *
   * @returns {ReactElement|null}
   */
  renderList() {
    const {
      disabled,
      options
    } = this.props;

    const {
      isVisible,
      elementHeight,
      elementWidth,
      rightAligned,
      bottomAligned
    } = this.state;

    if (isVisible && !disabled && options.length) {
      return (
        <div
          className={`${BLOCK_NAME}__list-wrapper`}
          style={{
            [bottomAligned ? "bottom" : "top"]: elementHeight + 8
          }}
        >
          <ul
            className={classnames(`${BLOCK_NAME}__list`, {
              [`${BLOCK_NAME}__list--align-bottom`]: bottomAligned,
              [`${BLOCK_NAME}__list--align-right`]: rightAligned
            })}
            onKeyDown={this.handleKeyDown}
            style={{
              maxWidth: this.RESULTS_MAX_WIDTH,
              minWidth: elementWidth > this.RESULTS_MIN_WIDTH
                ? elementWidth
                : this.RESULTS_MIN_WIDTH,
              maxHeight: this.RESULTS_MAX_HEIGHT
            }}
            ref={this.listElementRef}
            role="listbox"
          >
            {this.getOptionsList()}
          </ul>
        </div>
      );
    }

    return null;
  }

  /** @returns {ReactElement} */
  render() {
    const {
      customClassName,
      isInvalid,
      isSmall,
      isSmallish,
      disabled,
      isDark,
      noBorder,
      options,
      customButton,
      showIconAsSelected,
      showIconBesideSelected,
      size
    } = this.props;

    const {
      isVisible
    } = this.state;

    return (
      <div
        className={classnames(`${BLOCK_NAME}`, {
          [customClassName]: customClassName,
          [`${BLOCK_NAME}--invalid`]: isInvalid,
          [`${BLOCK_NAME}--size-small`]: isSmall || size === "small",
          [`${BLOCK_NAME}--disabled`]: disabled || !options.length,
          [`${BLOCK_NAME}--size-smallish`]: isSmallish || size === "smallish",
          [`${BLOCK_NAME}--size-x-small`]: size === "x-small"
        })}
        ref={this.selectElementRef}
        onMouseDown={this.handleToggleDropdown}
        onKeyDown={this.handleSelectKeyDown}
        tabIndex={(disabled || !options.length) ? "-1" : "0"}
        role="button"
      >
        {customButton ? (
          <SvgIcon
            customClassName={classnames(`${BLOCK_NAME}__custom-button`, {
              [`${BLOCK_NAME}__custom-button--active`]: isVisible
            })}
            icon={customButton}
          />
        ) : (
          <div
            className={classnames(`${BLOCK_NAME}__select`, {
              [`${BLOCK_NAME}__select--no-border`]: noBorder
            })}
          >
            {!showIconAsSelected && (
              <div className={`${BLOCK_NAME}__select__hidden-options`}>
                {this.renderAllHiddenOptions()}
              </div>
            )}
            <div
              className={classnames(`${BLOCK_NAME}__select__text`, {
                [`${BLOCK_NAME}__select__text--placeholder`]: !this.getSelectedOption(),
                [`${BLOCK_NAME}__select__text--relative`]: showIconAsSelected,
                [`${BLOCK_NAME}__select__text--icon-visible`]: showIconBesideSelected
              })}
            >
              {this.getSelectText()}
            </div>
          </div>
        )}
        {this.renderList()}
        {!customButton && (
          <div className={`${BLOCK_NAME}__icon-container`}>
            <SvgIcon
              icon="ChevronDown"
              customClassName={`${BLOCK_NAME}__icon-container__icon`}
              fillColor={isDark ? colors.colorBlack : colors.colorGrey4}
            />
          </div>
        )}
      </div>
    );
  }
}

Select.defaultProps = {
  isInvalid: false,
  noBorder: false,
  isDark: false,
  customClassName: null,
  isSmall: false,
  isSmallish: false,
  disabled: false,
  placeholder: null,
  placeholderIcon: null,
  placeholderIconColor: null,
  customButton: null,
  showIconAsSelected: null,
  value: null,
  subMenuValue: null,
  onToggleChange: NO_OP_FUNCTION,
  toggleValues: null,
  useRadioButtons: false,
  plainDropdown: false,
  showIconBesideSelected: false,
  size: "medium",
  forceRightAligned: false,
  forceBottomAligned: false,
  resultsMaxHeight: 165
};

Select.propTypes = {
  /** Specifies if Select value is valid or not */
  isInvalid: PropTypes.bool,
  /** Display Select within a border */
  noBorder: PropTypes.bool,
  /** Use dark theme */
  isDark: PropTypes.bool,
  /** CSS overrided */
  customClassName: PropTypes.string,
  /** Small style for Select */
  isSmall: PropTypes.bool,
  /** Smallish style for Select */
  isSmallish: PropTypes.bool,
  /** Use Radio Buttons instead of checkmarks */
  useRadioButtons: PropTypes.bool,
  /** Dropdown with no icons (checkmark/radio) */
  plainDropdown: PropTypes.bool,
  /** Handle select onChange event */
  onChange: PropTypes.func.isRequired,
  /** Disable the selector */
  disabled: PropTypes.bool,
  /** Placeholder option that cannot be selected */
  placeholder: PropTypes.string,
  /** display a custom icon in front of the placeholder */
  placeholderIcon: PropTypes.string,
  /** custom icon color code of the placeholder */
  placeholderIconColor: PropTypes.string,
  /** Use a custom icon instead of text for button */
  customButton: PropTypes.string,
  /** Show icon as the selected value instead of the label */
  showIconAsSelected: PropTypes.bool,
  /** Show icon beside the selected value */
  showIconBesideSelected: PropTypes.bool,
  /** The selected option */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** The selected submenu option */
  subMenuValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Array of select options that can be chosen */
  options: PropTypes.oneOfType([PropTypes.array, ImmutablePropTypes.list])
    .isRequired,
  /** Handle toggling toggle values on/off */
  onToggleChange: PropTypes.func,
  /** Values that can be toggled on/off */
  toggleValues: ImmutablePropTypes.map,
  size: PropTypes.oneOf(["x-small", "small", "smallish", "medium"]),
  /** Wheter we should align to the right */
  forceRightAligned: PropTypes.bool,
  /** Wheter we should align the bottom of the dropdown */
  forceBottomAligned: PropTypes.bool,
  /** A height prop for the dropdown */
  resultsMaxHeight: PropTypes.number
};

export default Select;
